// src/ui/screens/academy.ts
// ACADEMY MODE: one coach, twelve children, several nets, one afternoon.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE ONE FAILURE THIS SCREEN EXISTS TO PREVENT
// ══════════════════════════════════════════════════════════════════════════════════════════════
// A ball bowled by Aarav recorded against Zainab's name. That contaminates TWO Passports at once —
// hers gains a delivery she never bowled, his loses one he did — and every gate downstream (workload,
// review queue, Passport evidence, the parent report) keys on `athlete_id`, so the error propagates
// silently and is very hard to unpick afterwards. Everything below is arranged around stopping it.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE CHAIN IS lane → session → athlete, AND IT ONLY RUNS THAT WAY
// ══════════════════════════════════════════════════════════════════════════════════════════════
// A lane (a net) is a property of a SESSION. A session names its athlete. So a net can be used to
// find a session, and the session says whose it is — but a net can never be used to NAME a child.
// The backend enforces this structurally: `reassignLane` takes a closed literal containing exactly
// `context_json`, so there is no field through which an athlete_id could travel, and its own note
// says "moving a lane never moves a child". This screen keeps the same shape: the lane control on a
// member row can change only that row's net, and the only way to change WHO a session is of is the
// correction panel at the bottom, which is a different call with a different affirmation.
//
// Two open sessions in one academy may not hold the same net, because a camera pointed at a net that
// holds two children cannot say whose clip it recorded. The server refuses it (in-batch as a 409,
// across batches as a named `refused` row, on a lane move as a 409). This device ALSO checks the
// draft before sending — not to replace that answer but so a coach finds out before twelve children
// are standing in nets. When the two disagree, the server's answer is the one rendered.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THERE IS NO FACE RECOGNITION HERE, AND NOTHING THAT LOOKS LIKE IT
// ══════════════════════════════════════════════════════════════════════════════════════════════
// No face-match endpoint exists on the backend, no "identify from frame", no auto-assign. Its absence
// is the feature, so this screen must not hint that such a thing exists or is coming — no "detecting
// athlete…", no confidence score, no suggested match.
//
// Human confirmation is structural on the server: `authorizeConfirmation` is the ONLY producer of a
// `ConfirmedIdentityPrincipal`, and it refuses unless `confirmed` is literal `true`. This module
// mirrors that with `HumanAffirmation`, a class with a PRIVATE constructor whose single minting site
// is a button that names the child out loud. A request to bind an athlete cannot be assembled here
// without one, so "confirm by default", "confirm the only candidate", "confirm all twelve" and
// "confirm because a QR token was present" are not features that were left out — they are requests
// this file cannot construct.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// BIB ASSIST IS A HINT. IT NARROWS NOTHING BY ITSELF, AND SAYS SO.
// ══════════════════════════════════════════════════════════════════════════════════════════════
// No bib or jersey number is stored anywhere in the schema. The server refuses to fake a filter — it
// returns the whole active roster and a `hint` object saying plainly that the number matched nothing
// stored. So this screen NEVER filters candidates locally either: doing so would manufacture exactly
// the confidence the backend went out of its way to withhold. The hint's own note is rendered
// verbatim, including "two athletes can wear the same number on different days".
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// A CORRECTION IS NOT A TOTAL MOVE, AND THIS SCREEN REFUSES TO IMPLY THAT IT IS
// ══════════════════════════════════════════════════════════════════════════════════════════════
// `correctAssignment` moves the deliveries and appends a trail. It CANNOT move the metric
// observations: migration 041's `sports_pl_freeze_ai_output` trigger raises on any update that
// changes an observation's athlete_id / session_id / delivery_id, so they are dismissed in the review
// log and left as rows under the previous athlete. The server reports that as
// `observations_left_under_previous_athlete`. This screen renders it under its own heading — "What
// did NOT move" — every time, including when it is zero, because a coach who believes a correction
// was total will not go and ask for the observations to be re-derived.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// PROVENANCE: NO ID ON THIS SCREEN IS COMPOSED ON THE DEVICE
// ══════════════════════════════════════════════════════════════════════════════════════════════
// Athlete ids come from GET …/identity/candidates. Session ids come from the batch response or from
// GET …/today. Both are carried as whole objects, never re-typed, and every control that acts on one
// shows where it came from. The two ids this device DOES mint are the idempotency keys —
// `client_session_uuid` per child and the batch's own `batch_key` — and they are minted once, when
// the child is added to the draft, and never regenerated. That is the entire replay guarantee: press
// Start twice and the second press writes nothing.

import { el, empty, field, mount, section, spinner } from '../dom';
import { refusalView } from '../refusal-view';
import { isRefusal, request, type ApiResult } from '../../api/client';
import { PRACTICE_NAMESPACE as NS } from '../../config';
import {
  CAMERA_ROLES, academyToday, confirmAssignment, listRosterCandidates, mintSessionQr,
  type AcademyTodayView, type AssignmentMethod, type AssignmentResult, type CameraRole,
  type CandidatesResult, type RosterCandidate, type SessionQr,
  SESSION_MODES,
  type SessionMode,
} from '../../api/practice';
import { getState, setAcademy } from '../state';
import { deviceId, newSessionUuid } from '../../device/ids';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 1 · THE TYPED SURFACE THIS SCREEN ADDS, transcribed from the routers.
 *
 * `src/api/practice.ts` covers the roster, the assignment and the coach's day view. The four calls
 * below are the rest of Academy Mode, and they are declared HERE rather than there for one reason:
 * five other screens are being built against that file at this moment and it is not this lane's to
 * edit. They go through the same `request()` — one place a request leaves this app, one `Refusal`
 * type, no second transport.
 *
 * Sources: dcs-sports-backend/sb/src/practice/routes/academy-practice.ts (batch, lane),
 *          dcs-sports-backend/sb/src/practice/routes/identity.ts (corrections),
 *          and the service files behind them for the response shapes.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** MAX_BATCH_ATHLETES in academy-service.ts. Larger than this "is an import, not an afternoon". */
export const MAX_BATCH_ATHLETES = 24;

/** `normaliseLane` in academy-service.ts trims and refuses anything longer than this. */
export const MAX_LANE_LABEL = 32;

export interface BatchEntryBody {
  athlete_id: string;
  lane: string | null;
  /** 041 uniques (client_device_id, client_session_uuid). One child, one key, forever. */
  client_session_uuid: string;
}

export interface StartBatchBody {
  /** Stable across replays of the same afternoon. A correlation tag, not an identity. */
  batch_key: string;
  client_device_id: string;
  entries: BatchEntryBody[];
  defaults: { mode: string; camera_role: CameraRole };
}

export interface BatchMember {
  athlete_id: string;
  lane: string | null;
  session_id: string;
  /** TRUE means the row already existed and nothing was written. */
  replayed: boolean;
  subject_is_minor: boolean;
  /** From the identity lane. FALSE until a human confirms — never implied by being in a batch. */
  identity_confirmed: boolean;
}

/** Partial success is deliberate: one child who left the academy last week must not refuse eleven. */
export interface BatchRefusal {
  athlete_id: string | null;
  lane: string | null;
  code: number;
  reason: string;
}

export interface BatchView {
  batch_key: string;
  academy_id: string;
  coach_id: string;
  started_at: string;
  members: BatchMember[];
  refused: BatchRefusal[];
  note: string;
}

/** POST …/batch-sessions — 201. N sessions, one per child, sharing a correlation key. */
export function startBatch(academyId: string, body: StartBatchBody): Promise<ApiResult<BatchView>> {
  return request(`${NS}/academies/${encodeURIComponent(academyId)}/batch-sessions`, { method: 'POST', body });
}

/** GET …/batch-sessions/:batchKey — every member RE-GATED on read, so a departure is visible. */
export function readBatch(academyId: string, batchKey: string): Promise<ApiResult<BatchView>> {
  return request(
    `${NS}/academies/${encodeURIComponent(academyId)}/batch-sessions/${encodeURIComponent(batchKey)}`,
  );
}

export interface LaneAssignmentView {
  session_id: string;
  athlete_id: string;
  lane: string | null;
  previous_lane: string | null;
  lane_history: Array<{ lane: string | null; at: string; by: string }>;
  identity_confirmed: boolean;
  /** The server's own sentence about what a lane is and is not. Rendered verbatim. */
  note: string;
}

/**
 * PUT …/sessions/:sessionId/lane — move ONE session to a different net.
 *
 * The signature is the guarantee: `sessionId` and a lane label, and there is no parameter here
 * through which an athlete could travel. Changing who a session is of is `correctAthlete`, below.
 */
export function setLane(
  academyId: string, sessionId: string, lane: string | null,
): Promise<ApiResult<LaneAssignmentView>> {
  return request(
    `${NS}/academies/${encodeURIComponent(academyId)}/sessions/${encodeURIComponent(sessionId)}/lane`,
    { method: 'PUT', body: { lane } },
  );
}

export interface CorrectionReceipt {
  from_session_id: string;
  from_athlete_id: string;
  to_session_id: string;
  to_athlete_id: string;
  corrected_by: string;
  corrected_at: string;
  deliveries_moved: number;
  reviews_appended: number;
  observations_dismissed: number;
  /**
   * THE NUMBER THIS SCREEN WILL NOT BURY. Metric observations cannot be re-pointed — 041's
   * `sports_pl_freeze_ai_output` forbids it — so they are dismissed and left as rows under the
   * previous athlete, and must be re-derived for the correct one.
   */
  observations_left_under_previous_athlete: number;
  /** Anything the schema would not let the service finish. Not decoration; rendered in full. */
  notes: string[];
}

export interface CorrectionBody {
  from_session_id: string;
  from_athlete_id: string;
  to_session_id: string;
  to_athlete_id: string;
  method: AssignmentMethod;
  /** Typed `true`, so this client cannot express the request the server 409s on. */
  confirmed: true;
  reason?: string | null;
  qr_token?: string | null;
  device_id?: string | null;
}

/** POST …/identity/corrections — an APPEND, never a patch. The prior binding survives. */
export function correctAthlete(academyId: string, body: CorrectionBody): Promise<ApiResult<CorrectionReceipt>> {
  return request(
    `${NS}/academies/${encodeURIComponent(academyId)}/identity/corrections`, { method: 'POST', body },
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 2 · THE HUMAN AFFIRMATION — a value only one private site can mint
 *
 * The backend's `authorizeConfirmation` is the only producer of a `ConfirmedIdentityPrincipal`
 * anywhere in that codebase; every path that assigns a child goes through it. That is the property
 * worth mirroring, because it is what makes "a human confirmed this" impossible to forget rather
 * than merely required by a comment.
 *
 * `HumanAffirmation` has a PRIVATE constructor. `HumanAffirmation.affirm()` is its only minting
 * site, and it is called from exactly one place: the click handler of a button whose visible label
 * names the child. The functions that send an assignment or a correction take a `HumanAffirmation`
 * and read the athlete id, the session id and the method OUT OF IT — they accept no separate athlete
 * argument at all. So there is no expression in this module that confirms an athlete a human did not
 * name, and a future edit that tried to add one would have to defeat the constructor first.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** A session a human may act on. Every field came out of a server response; see the header block. */
export interface ConfirmTarget {
  readonly session_id: string;
  readonly athlete_id: string;
  /** What the coach sees. The roster's display name where there is one, else the id. */
  readonly label: string;
  readonly lane: string | null;
  readonly subject_is_minor: boolean | null;
  /** Which server response produced this row, shown on screen so nothing is taken on trust. */
  readonly source: 'batch_sessions' | 'academy_today';
}

export class HumanAffirmation {
  private constructor(
    private readonly minted_at: string,
    readonly session_id: string,
    readonly athlete_id: string,
    readonly athlete_label: string,
    readonly method: AssignmentMethod,
    readonly qr_token: string | null,
  ) {}

  /** When the person pressed it. Recorded so a receipt can be checked against the act. */
  get at(): string { return this.minted_at; }

  /**
   * THE ONLY MINTING SITE. Deliberately takes the whole target rather than loose ids, so the thing
   * affirmed is the row that was on screen — not a re-assembled one that might have drifted.
   *
   * `SESSION_QR` still requires this: a token binds a device to an athlete for a session, and the
   * server treats it as EVIDENCE, not as an assignment. Presenting one is not confirming.
   */
  static affirm(target: ConfirmTarget, method: AssignmentMethod, qrToken: string | null = null): HumanAffirmation {
    return new HumanAffirmation(
      new Date().toISOString(), target.session_id, target.athlete_id, target.label, method,
      method === 'SESSION_QR' ? qrToken : null,
    );
  }
}

/**
 * Send the assignment. Takes no athlete argument — the subject is read out of the affirmation.
 *
 * `confirmed: true` is a literal, and `confirmAssignment`'s type in api/practice.ts forbids anything
 * else, so the 409 the server raises on a non-`true` value is a request this app cannot make.
 */
export function confirmWithAffirmation(
  academyId: string, a: HumanAffirmation, deviceIdValue?: string | null,
): Promise<ApiResult<AssignmentResult>> {
  return confirmAssignment(academyId, {
    session_id: a.session_id,
    athlete_id: a.athlete_id,
    method: a.method,
    confirmed: true,
    ...(a.qr_token ? { qr_token: a.qr_token } : {}),
    ...(deviceIdValue ? { device_id: deviceIdValue } : {}),
  });
}

/**
 * The correction's affirmation names BOTH children, because both are subjects of it: one loses the
 * record, one gains it, and the server gates both sides independently before a delivery moves.
 */
export class CorrectionAffirmation {
  private constructor(
    private readonly minted_at: string,
    readonly from: ConfirmTarget,
    readonly to: ConfirmTarget,
    readonly method: AssignmentMethod,
    readonly reason: string | null,
  ) {}

  get at(): string { return this.minted_at; }

  static affirm(
    from: ConfirmTarget, to: ConfirmTarget, method: AssignmentMethod, reason: string | null,
  ): CorrectionAffirmation {
    return new CorrectionAffirmation(new Date().toISOString(), from, to, method, reason);
  }
}

export function correctWithAffirmation(
  academyId: string, a: CorrectionAffirmation,
): Promise<ApiResult<CorrectionReceipt>> {
  return correctAthlete(academyId, {
    from_session_id: a.from.session_id,
    from_athlete_id: a.from.athlete_id,
    to_session_id: a.to.session_id,
    to_athlete_id: a.to.athlete_id,
    method: a.method,
    confirmed: true,
    reason: a.reason,
  });
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 3 · THE BATCH DRAFT — held on this device until the coach sends it
 *
 * The draft is the afternoon as the coach has planned it: these children, in these nets. It is not
 * data about anybody — it is a list of ids the server already gave us plus the keys this device
 * minted. Nothing in it is displayed as if it had been recorded.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface BatchEntryDraft {
  readonly athlete_id: string;
  readonly label: string;
  readonly subject_is_minor: boolean | null;
  /** Editable. The one field of a draft entry that a coach changes. */
  lane: string | null;
  /**
   * MINTED ONCE, at the moment this child was added, and never regenerated.
   *
   * Regenerating it on retry is the mistake that makes idempotency perfect and useless: the server
   * would see a different key, create a second session, and be right to.
   */
  readonly client_session_uuid: string;
}

export interface BatchDraft {
  readonly academy_id: string;
  /** Also minted once. Replaying the same afternoon must carry the same tag or it is a new one. */
  readonly batch_key: string;
  entries: BatchEntryDraft[];
}

export function newDraft(academyId: string): BatchDraft {
  return { academy_id: academyId, batch_key: `batch-${newSessionUuid()}`, entries: [] };
}

/**
 * Add a child, FROM A ROSTER ROW. The parameter is the candidate object the server sent, not a
 * string, so an id that was typed, guessed or carried over from another academy cannot get in.
 */
export function addToDraft(draft: BatchDraft, c: RosterCandidate): BatchDraft {
  if (draft.entries.some((e) => e.athlete_id === c.athlete_id)) return draft;
  draft.entries.push({
    athlete_id: c.athlete_id,
    label: (typeof c.display_name === 'string' && c.display_name) || c.athlete_id,
    subject_is_minor: typeof c.subject_is_minor === 'boolean' ? c.subject_is_minor : null,
    lane: null,
    client_session_uuid: newSessionUuid(),
  });
  return draft;
}

export function removeFromDraft(draft: BatchDraft, athleteId: string): BatchDraft {
  draft.entries = draft.entries.filter((e) => e.athlete_id !== athleteId);
  return draft;
}

export type DraftProblemCode =
  | 'EMPTY' | 'TOO_MANY' | 'LANE_TOO_LONG' | 'LANE_TWICE' | 'DUPLICATE_ATHLETE' | 'DUPLICATE_UUID';

export interface DraftProblem { code: DraftProblemCode; message: string }

/**
 * THE PRE-FLIGHT, and it is honestly labelled as one on screen.
 *
 * These are the server's own rules restated on the device so a coach learns about a doubled net
 * before twelve children are standing in them. It does not decide anything: the request is still
 * sent when the coach insists, and the server's refusal is what gets rendered. A client-side check
 * that quietly replaced the server's answer would be the "cannot-fail control" defect — a gate whose
 * pass and fail branches are indistinguishable from outside.
 */
export function checkDraft(draft: BatchDraft): DraftProblem[] {
  const problems: DraftProblem[] = [];
  if (draft.entries.length === 0) {
    problems.push({ code: 'EMPTY', message: 'Nobody is in this batch yet. Tap roster rows to add children.' });
  }
  if (draft.entries.length > MAX_BATCH_ATHLETES) {
    problems.push({
      code: 'TOO_MANY',
      message: `A batch may cover at most ${MAX_BATCH_ATHLETES} athletes — larger than that is an import, not an afternoon.`,
    });
  }
  const lanes = new Map<string, string>();
  const athletes = new Set<string>();
  const uuids = new Set<string>();
  for (const e of draft.entries) {
    if (athletes.has(e.athlete_id)) {
      problems.push({ code: 'DUPLICATE_ATHLETE', message: `${e.label} is in this batch twice — one child, one session.` });
    }
    athletes.add(e.athlete_id);

    if (uuids.has(e.client_session_uuid)) {
      problems.push({
        code: 'DUPLICATE_UUID',
        message: 'Two entries share a client_session_uuid — one child\'s session would silently overwrite the other\'s.',
      });
    }
    uuids.add(e.client_session_uuid);

    const lane = (e.lane ?? '').trim();
    if (!lane) continue;
    if (lane.length > MAX_LANE_LABEL) {
      problems.push({ code: 'LANE_TOO_LONG', message: `Net "${lane}" is longer than ${MAX_LANE_LABEL} characters.` });
      continue;
    }
    const holder = lanes.get(lane);
    if (holder) {
      problems.push({
        code: 'LANE_TWICE',
        message: `Net ${lane} is claimed by both ${holder} and ${e.label}. A net holds one athlete, `
          + 'or a camera pointed at it cannot say whose clip it recorded.',
      });
      continue;
    }
    lanes.set(lane, e.label);
  }
  return problems;
}

/** The wire body. Lanes are trimmed to null when blank, exactly as `normaliseLane` would read them. */
export function batchBody(
  draft: BatchDraft, clientDeviceId: string, defaults: { mode: string; camera_role: CameraRole },
): StartBatchBody {
  return {
    batch_key: draft.batch_key,
    client_device_id: clientDeviceId,
    entries: draft.entries.map((e) => ({
      athlete_id: e.athlete_id,
      lane: (e.lane ?? '').trim() || null,
      client_session_uuid: e.client_session_uuid,
    })),
    defaults,
  };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 4 · SCREEN STATE
 *
 * Module-level because `rerender()` re-invokes the screen function from the top, exactly as the
 * coach screen relies on `ui/state.ts` surviving a re-render. Everything here is either a server
 * response held verbatim or a draft the coach is composing. No derived athlete data is cached.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

let draft: BatchDraft | null = null;
let bibHint = '';
let mode: SessionMode = 'LIVE_BOWLER';   // see SESSION_MODES — NET_PRACTICE was never a mode
let cameraRole: CameraRole = 'BALL_VIEW';
/** The server's own last batch response. The ONLY source of session ids on this screen. */
let lastBatch: BatchView | null = null;
/** The row a human has selected to act on. Selection is not confirmation; see the header block. */
let selectedSessionId: string | null = null;
let qr: SessionQr | null = null;
let correctionFromId: string | null = null;
let correctionToId: string | null = null;
let correctionReason = '';

/** Test seam and academy-switch reset. Nothing in the product carries a draft across academies. */
export function __resetAcademyScreen(): void {
  draft = null;
  bibHint = '';
  mode = 'LIVE_BOWLER';   // must match the module default, or a reset restores the old defect
  cameraRole = 'BALL_VIEW';
  lastBatch = null;
  selectedSessionId = null;
  qr = null;
  correctionFromId = null;
  correctionToId = null;
  correctionReason = '';
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 5 · THE SCREEN
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export async function academyScreen(root: HTMLElement, rerender: () => void): Promise<void> {
  const st = getState();
  const body = el('div');

  const academyInput = el('input', {
    class: 'input mono', type: 'text', value: st.academyId ?? '', placeholder: 'academy id (uuid)',
  });

  mount(root,
    section('Academy Mode — one coach, several nets, one afternoon',
      el('p', { class: 'note' },
        'A batch is N practice sessions, one per child, sharing a correlation tag. Every gate '
        + 'downstream keys on the athlete, so there is no such thing as a session for "the group".'),
      el('p', { class: 'note' },
        'The academy id is typed, and that is safe: the server checks you own this academy before it '
        + 'returns a single roster row. A wrong id gets a refusal, never another academy’s children.'),
      academyInput,
      el('button', {
        class: 'btn btn-primary',
        onclick: () => {
          const next = academyInput.value.trim();
          if (next !== (st.academyId ?? '')) __resetAcademyScreen();
          setAcademy(next);
          rerender();
        },
      }, 'Load academy'),
    ),
    body,
  );

  if (!st.academyId) {
    body.append(empty('No academy loaded. Enter an academy id above to see today’s nets and the roster.'));
    return;
  }
  const academyId = st.academyId;
  if (draft && draft.academy_id !== academyId) __resetAcademyScreen();
  if (!draft) draft = newDraft(academyId);

  body.append(spinner('Loading today’s nets and the roster…'));
  const [today, roster] = await Promise.all([
    academyToday(academyId),
    listRosterCandidates(academyId, bibHint.trim() || undefined),
  ]);

  const targets = confirmTargets(today, lastBatch, roster);

  const out = el('div');
  out.append(dayView(today, rerender));
  out.append(rosterPanel(roster, rerender));
  out.append(draftPanel(academyId, rerender));
  out.append(batchResultPanel(rerender));
  out.append(lanePanel(academyId, targets, rerender));
  out.append(identityPanel(academyId, targets, rerender));
  out.append(correctionPanel(academyId, targets, rerender));
  mount(body, out);
}

/**
 * Everything a human may act on, assembled ONLY from server responses.
 *
 * The batch response and the day view are both authoritative; where they name the same session the
 * batch wins because it carries the lane the server just recorded. `roster` supplies display names —
 * and only display names, matched by an id the server already returned in both places.
 */
export function confirmTargets(
  today: ApiResult<AcademyTodayView>,
  batch: BatchView | null,
  roster: ApiResult<CandidatesResult> | null,
): ConfirmTarget[] {
  const names = new Map<string, string>();
  if (roster && !isRefusal(roster)) {
    for (const c of roster.value.candidates ?? []) {
      if (typeof c.display_name === 'string' && c.display_name) names.set(c.athlete_id, c.display_name);
    }
  }
  const byId = new Map<string, ConfirmTarget>();
  if (!isRefusal(today)) {
    for (const a of today.value.athletes) {
      for (const s of a.sessions_today) {
        if (s.completed) continue; // a finished session cannot change nets and needs no new binding
        byId.set(s.session_id, {
          session_id: s.session_id,
          athlete_id: a.athlete_id,
          label: names.get(a.athlete_id) ?? a.athlete_id,
          lane: s.lane,
          subject_is_minor: a.subject_is_minor,
          source: 'academy_today',
        });
      }
    }
  }
  for (const m of batch?.members ?? []) {
    byId.set(m.session_id, {
      session_id: m.session_id,
      athlete_id: m.athlete_id,
      label: names.get(m.athlete_id) ?? m.athlete_id,
      lane: m.lane,
      subject_is_minor: m.subject_is_minor,
      source: 'batch_sessions',
    });
  }
  return [...byId.values()];
}

/* ── THE COACH'S DAY ──────────────────────────────────────────────────────────────────────── */

function dayView(today: ApiResult<AcademyTodayView>, rerender: () => void): HTMLElement {
  if (isRefusal(today)) return section('Today', refusalView(today, { onRetry: rerender }));
  const t = today.value;

  const rows = el('div', { class: 'list' });
  for (const a of t.athletes) {
    const nets = a.sessions_today.map((s) => s.lane).filter((l): l is string => !!l);
    rows.append(el('div', { class: 'row-item' },
      el('p', { class: 'mono small' }, a.athlete_id),
      a.subject_is_minor && el('span', { class: 'tag tag-minor' }, 'minor'),
      field('Sessions today', a.sessions_today.length),
      // The chain, shown as a chain: the net is a property of the session, which names the child.
      field('Nets', nets.length ? nets.join(', ') : null),
      field('Pending reviews', a.pending_reviews),
      field('Workload', a.workload_state),
      // The workload guardian's own closed set of sentences, unedited.
      ...a.workload_messages.map((msg) => el('p', { class: 'note' }, msg)),
      a.needs_identity_confirmation && el('p', { class: 'warn' },
        'A session is running with no human-confirmed identity behind it. Until someone confirms it, '
        + 'nothing recorded in that net can be trusted to belong to this child.'),
    ));
  }

  return section('Today',
    field('Athletes you may read', t.athlete_count),
    field('In a net now', t.totals.in_session_now),
    field('Pending reviews', t.totals.pending_reviews),
    field('Workload flagged', t.totals.workload_flagged),
    field('Unconfirmed identity', t.totals.unconfirmed_identity),
    // Surfaced, not absorbed: the backend reports both numbers on purpose.
    t.roster_candidates_seen !== t.athlete_count && el('p', { class: 'warn' },
      `${t.roster_candidates_seen} athletes are on this roster but only ${t.athlete_count} are ones `
      + 'you may read. The difference is shown rather than hidden.'),
    el('p', { class: 'note' }, t.note),
    t.athletes.length ? rows : empty('No athlete rows in today’s view.'),
  );
}

/* ── ROSTER + BIB ASSIST ──────────────────────────────────────────────────────────────────── */

function rosterPanel(roster: ApiResult<CandidatesResult>, rerender: () => void): HTMLElement {
  const bibInput = el('input', {
    class: 'input', type: 'text', value: bibHint, placeholder: 'bib / jersey number (a hint only)',
  });
  const assist = el('div', {},
    el('h3', {}, 'Bib assist'),
    el('p', { class: 'note' },
      'No bib or jersey number is stored anywhere in this system. A number here is sent to the '
      + 'server as a hint and recorded as evidence; the server answers honestly that it narrowed '
      + 'nothing rather than pretending to filter, and this screen never filters the list itself.'),
    bibInput,
    el('button', {
      class: 'btn',
      onclick: () => { bibHint = bibInput.value; rerender(); },
    }, 'Send as a hint'),
    bibHint.trim() && el('button', {
      class: 'btn btn-quiet',
      onclick: () => { bibHint = ''; rerender(); },
    }, 'Clear the hint'),
  );

  if (isRefusal(roster)) {
    return section('Roster', assist, refusalView(roster, { onRetry: rerender }));
  }

  const candidates = roster.value.candidates ?? [];
  const hint = roster.value.hint as
    { kind?: string; value?: string; narrowed?: boolean; note?: string } | null | undefined;

  const list = el('div', { class: 'list' });
  for (const c of candidates) {
    const inDraft = !!draft?.entries.some((e) => e.athlete_id === c.athlete_id);
    const label = (typeof c.display_name === 'string' && c.display_name) || c.athlete_id;
    list.append(el('button', {
      class: `row-item row-tap${inDraft ? ' row-selected' : ''}`,
      onclick: () => {
        if (!draft) return;
        if (inDraft) removeFromDraft(draft, c.athlete_id);
        else addToDraft(draft, c);
        rerender();
      },
    },
      el('p', { class: 'mono small' }, label),
      c.subject_is_minor && el('span', { class: 'tag tag-minor' }, 'minor'),
      // Rendered from the server's own flag, not assumed true because we know it always is.
      c.requires_confirmation && el('span', { class: 'tag' }, 'needs human confirmation'),
      inDraft ? el('span', { class: 'tag tag-ok' }, 'in this batch') : el('span', { class: 'tag' }, 'tap to add'),
    ));
  }

  return section('Roster — tap to add a child to the batch',
    el('p', { class: 'note' },
      'Tapping adds a child to the DRAFT below. It records nothing and confirms nobody: starting a '
      + 'session and confirming who is in the net are two later, separate acts.'),
    assist,
    hint && el('div', {},
      field('Hint', typeof hint.value === 'string' ? hint.value : null),
      field('Narrowed the roster', hint.narrowed ? 'yes' : 'no'),
      // The server's own sentence. It is the whole point of the hint being honest.
      typeof hint.note === 'string' && el('p', { class: 'note' }, hint.note),
    ),
    field('Candidates returned', candidates.length),
    candidates.length ? list : empty('This academy’s roster came back empty.'),
  );
}

/* ── THE DRAFT: children, nets, and the keys this device minted ───────────────────────────── */

function draftPanel(academyId: string, rerender: () => void): HTMLElement {
  const d = draft;
  if (!d) return section('Batch draft', empty('No draft.'));

  const problemsNode = el('div');
  const redrawProblems = (): void => {
    const problems = checkDraft(d);
    mount(problemsNode,
      el('h3', {}, 'Before sending — this device’s own check'),
      el('p', { class: 'note' },
        'These are the server’s rules restated here so a doubled net is caught before twelve '
        + 'children are standing in them. The server checks again and its answer is the one that '
        + 'counts — nothing below is suppressed on this device’s say-so.'),
      problems.length
        ? el('div', { class: 'list' }, ...problems.map((p) => el('p', { class: 'warn' }, p.message)))
        : el('p', { class: 'ok' }, 'Nothing this device can see wrong with the draft.'),
    );
  };

  const rows = el('div', { class: 'list' });
  for (const e of d.entries) {
    const laneInput = el('input', {
      class: 'input', type: 'text', value: e.lane ?? '', placeholder: 'net / lane (optional)',
    });
    laneInput.addEventListener('input', () => {
      // The draft is edited in place and the problem list is redrawn, WITHOUT a full re-render:
      // re-rendering on every keystroke would drop focus mid-word on a phone.
      e.lane = laneInput.value.trim() || null;
      redrawProblems();
    });
    rows.append(el('div', { class: 'row-item' },
      el('p', { class: 'mono small' }, e.label),
      e.subject_is_minor && el('span', { class: 'tag tag-minor' }, 'minor'),
      el('p', { class: 'mono small' }, e.athlete_id),
      el('label', { class: 'label' }, 'Net'), laneInput,
      // Shown, because it is the reason a second press writes nothing.
      field('Idempotency key', e.client_session_uuid),
      el('button', {
        class: 'btn btn-quiet',
        onclick: () => { removeFromDraft(d, e.athlete_id); rerender(); },
      }, `Remove ${e.label}`),
    ));
  }

  // A select, not free text — see SESSION_MODES. A typed mode is how every batch session on this
  // screen failed: the box accepted any string, and the server accepts exactly four.
  const modeInput = el('select', { class: 'input' },
    ...SESSION_MODES.map((m) => el('option', { value: m, ...(m === mode ? { selected: true } : {}) }, m)));
  modeInput.addEventListener('change', () => { mode = modeInput.value as SessionMode; });
  const roleSelect = el('select', { class: 'input' },
    ...CAMERA_ROLES.map((r) => el('option', { value: r, ...(r === cameraRole ? { selected: true } : {}) }, r)));
  roleSelect.addEventListener('change', () => { cameraRole = roleSelect.value as CameraRole; });

  const result = el('div');
  const send = async (): Promise<void> => {
    mount(result, spinner('Starting the batch…'));
    const device = await deviceId();
    const r = await startBatch(academyId, batchBody(d, device, { mode, camera_role: cameraRole }));
    if (isRefusal(r)) { mount(result, refusalView(r)); return; }
    lastBatch = r.value;
    mount(result, el('p', { class: 'ok' },
      `The server accepted the batch. ${r.value.members.length} session(s), ${r.value.refused.length} refusal(s).`));
    rerender();
  };

  redrawProblems();

  return section('Batch draft — these children, these nets',
    field('Batch key', d.batch_key),
    el('p', { class: 'note' },
      'The batch key and every child’s session key were minted on this device when the child was '
      + 'added, and are never regenerated. Pressing Start twice sends the identical request, so the '
      + 'second press returns the same sessions and writes nothing.'),
    el('h3', {}, 'Capture settings for every session in this batch'),
    el('label', { class: 'label' }, 'Mode'), modeInput,
    el('label', { class: 'label' }, 'Camera role'), roleSelect,
    d.entries.length ? rows : empty('Nobody in the draft yet. Tap roster rows above.'),
    problemsNode,
    el('button', {
      class: 'btn btn-primary',
      onclick: () => { void send(); },
    }, `Start ${d.entries.length} session(s)`),
    result,
    el('button', {
      class: 'btn',
      onclick: () => {
        void (async () => {
          mount(result, spinner('Reading the batch back…'));
          const r = await readBatch(academyId, d.batch_key);
          if (isRefusal(r)) { mount(result, refusalView(r)); return; }
          lastBatch = r.value;
          mount(result, el('p', { class: 'ok' }, 'Read back from the server.'));
          rerender();
        })();
      },
    }, 'Read this batch back'),
    el('button', {
      class: 'btn btn-quiet',
      onclick: () => { __resetAcademyScreen(); rerender(); },
    }, 'Start a new afternoon (new batch key)'),
  );
}

function batchResultPanel(rerender: () => void): HTMLElement {
  const b = lastBatch;
  if (!b) {
    return section('Batch', empty('No batch has been started or read back in this session yet.'));
  }
  const members = el('div', { class: 'list' });
  for (const m of b.members) {
    members.append(el('div', { class: 'row-item' },
      el('p', { class: 'mono small' }, m.athlete_id),
      field('Session', m.session_id),
      field('Net', m.lane),
      m.subject_is_minor && el('span', { class: 'tag tag-minor' }, 'minor'),
      m.replayed
        ? el('span', { class: 'tag tag-ok' }, 'replayed — nothing was written')
        : el('span', { class: 'tag' }, 'created'),
      // NEVER implied by membership of a batch. The identity lane is the only thing that sets it.
      m.identity_confirmed
        ? el('span', { class: 'tag tag-ok' }, 'identity confirmed')
        : el('span', { class: 'tag tag-held' }, 'identity NOT confirmed'),
    ));
  }

  const refused = el('div', { class: 'list' });
  for (const r of b.refused) {
    refused.append(el('div', { class: 'row-item' },
      el('p', { class: 'mono small' }, r.athlete_id ?? '(no athlete id)'),
      field('Net', r.lane),
      field('Status', r.code),
      // The server's own sentence for why this child is not in a net.
      el('p', { class: 'warn' }, r.reason),
    ));
  }

  return section('Batch — what the server did',
    field('Batch key', b.batch_key),
    field('Started at', b.started_at),
    field('Sessions', b.members.length),
    field('Refused', b.refused.length),
    b.members.length ? members : empty('No sessions in this batch.'),
    b.refused.length
      ? el('div', {},
          el('h3', {}, 'Refused — named, not silently dropped'),
          el('p', { class: 'note' },
            'A batch succeeds partially on purpose: one child who left the academy last week must '
            + 'not refuse the other eleven. Every refusal is named so the absence is visible.'),
          refused)
      : null,
    el('p', { class: 'note' }, b.note),
    el('button', { class: 'btn btn-quiet', onclick: rerender }, 'Refresh'),
  );
}

/* ── LANES ────────────────────────────────────────────────────────────────────────────────── */

function lanePanel(academyId: string, targets: ConfirmTarget[], rerender: () => void): HTMLElement {
  if (!targets.length) {
    return section('Nets', empty('No open sessions to move. Start a batch, or load today’s view.'));
  }
  const rows = el('div', { class: 'list' });
  for (const t of targets) {
    const laneInput = el('input', { class: 'input', type: 'text', value: t.lane ?? '', placeholder: 'net / lane' });
    const out = el('div');
    rows.append(el('div', { class: 'row-item' },
      el('p', { class: 'mono small' }, t.label),
      field('Session', t.session_id),
      field('Net now', t.lane),
      field('Row came from', t.source),
      laneInput,
      el('button', {
        class: 'btn',
        onclick: () => {
          void (async () => {
            mount(out, spinner('Moving this session to another net…'));
            const r = await setLane(academyId, t.session_id, laneInput.value.trim() || null);
            if (isRefusal(r)) { mount(out, refusalView(r)); return; }
            mount(out,
              el('p', { class: 'ok' }, `Session moved from ${r.value.previous_lane ?? 'no net'} to ${r.value.lane ?? 'no net'}.`),
              // The athlete is echoed to make visible that it did NOT change.
              field('Still this athlete', r.value.athlete_id),
              field('Identity confirmed', r.value.identity_confirmed ? 'yes' : 'no'),
              el('p', { class: 'note' }, r.value.note),
              el('button', { class: 'btn btn-quiet', onclick: rerender }, 'Refresh'));
          })();
        },
      }, `Move ${t.label}’s session to this net`),
      out,
    ));
  }

  return section('Nets — a lane is a property of a session',
    el('p', { class: 'note' },
      'The chain runs net → session → athlete and only that way. Moving a session to another net '
      + 'cannot move a child: to change WHO a session is of, use the correction panel below, which '
      + 'relocates the deliveries already recorded instead of orphaning them.'),
    el('p', { class: 'note' },
      'Two open sessions in this academy may not hold the same net. A camera pointed at a net with '
      + 'two children in it cannot say whose clip it recorded, so the server refuses the second claim.'),
    rows,
  );
}

/* ── IDENTITY: ROSTER TAP AND SESSION QR ──────────────────────────────────────────────────── */

function identityPanel(academyId: string, targets: ConfirmTarget[], rerender: () => void): HTMLElement {
  const intro = [
    el('p', { class: 'note' },
      'A person confirms who is in the net. There is no face matching in this product — no such '
      + 'endpoint exists on the server and none is planned — so the confirmation is exactly one '
      + 'human act, named and deliberate.'),
    el('p', { class: 'note' },
      'Selecting a session below selects. It confirms nobody. The confirm button appears only after '
      + 'a selection, names the child out loud, and there is deliberately no way to confirm more '
      + 'than one child at a time: a batch of twelve confirmations pressed at once is a habit, and a '
      + 'habit is what puts one child’s afternoon on another child’s record.'),
  ];

  if (!targets.length) {
    return section('Who is in the net', ...intro,
      empty('No open sessions to confirm. Start a batch, or load today’s view.'));
  }

  const rows = el('div', { class: 'list' });
  for (const t of targets) {
    rows.append(el('button', {
      class: `row-item row-tap${selectedSessionId === t.session_id ? ' row-selected' : ''}`,
      onclick: () => {
        selectedSessionId = t.session_id;
        qr = null; // a QR minted for another session is evidence about that session, never this one
        rerender();
      },
    },
      el('p', { class: 'mono small' }, t.label),
      field('Session', t.session_id),
      field('Net', t.lane),
      t.subject_is_minor && el('span', { class: 'tag tag-minor' }, 'minor'),
      selectedSessionId === t.session_id && el('span', { class: 'tag tag-ok' }, 'selected'),
    ));
  }

  const selected = targets.find((t) => t.session_id === selectedSessionId) ?? null;
  return section('Who is in the net', ...intro, rows,
    selected
      ? confirmBlock(academyId, selected, rerender)
      : empty('Nothing selected. Tap a session above to confirm who is in that net.'));
}

function confirmBlock(academyId: string, t: ConfirmTarget, rerender: () => void): HTMLElement {
  const out = el('div');
  const qrOut = el('div');

  const send = (method: AssignmentMethod, token: string | null): void => {
    void (async () => {
      mount(out, spinner('Confirming…'));
      // THE ONE MINTING SITE. It happens inside a click handler, on a button that named this child.
      const affirmation = HumanAffirmation.affirm(t, method, token);
      const device = await deviceId();
      const r = await confirmWithAffirmation(academyId, affirmation, device);
      if (isRefusal(r)) { mount(out, refusalView(r)); return; }
      mount(out,
        el('p', { class: 'ok' }, `Confirmed by ${r.value.confirmed_by} at ${r.value.confirmed_at}.`),
        field('Method', r.value.method),
        field('Session', r.value.session_id),
        field('Athlete', r.value.athlete_id),
        r.value.subject_is_minor && el('p', { class: 'warn' }, 'The subject is a minor.'),
        el('button', { class: 'btn btn-quiet', onclick: rerender }, 'Refresh'));
    })();
  };

  return el('div', {},
    el('h3', {}, `Confirm the child in net ${t.lane ?? '(no net)'}`),
    field('Session', t.session_id),
    field('Athlete id', t.athlete_id),
    field('Row came from', t.source),
    t.subject_is_minor && el('p', { class: 'warn' }, 'This athlete is a minor.'),

    el('h3', {}, 'Roster tap'),
    el('p', { class: 'note' },
      'You are looking at the child and saying so. The server still re-checks that you own this '
      + 'academy and that this athlete holds an active enrollment in it — your word alone is not '
      + 'authority, and neither is theirs alone.'),
    el('button', {
      class: 'btn btn-primary',
      onclick: () => { send('ROSTER_TAP', null); },
    }, `Confirm this is ${t.label}`),

    el('h3', {}, 'Session QR'),
    el('p', { class: 'note' },
      'A QR binds one device to one athlete for one session. It is signed EVIDENCE, not an '
      + 'assignment: minting it confirms nobody, and a person still has to press the confirm button '
      + 'below. The token is short-lived and the server clamps its lifetime.'),
    el('button', {
      class: 'btn',
      onclick: () => {
        void (async () => {
          mount(qrOut, spinner('Minting a session QR…'));
          const device = await deviceId();
          const r = await mintSessionQr(academyId, {
            session_id: t.session_id, athlete_id: t.athlete_id, device_id: device,
          });
          if (isRefusal(r)) { qr = null; mount(qrOut, refusalView(r)); return; }
          qr = r.value;
          mount(qrOut,
            field('Token', r.value.token),
            field('Expires at', r.value.expires_at),
            field('Valid for (seconds)', r.value.ttl_seconds),
            el('p', { class: 'note' },
              'Minted. Nobody has been assigned to anything — this is evidence a human can now '
              + 'stand behind.'));
        })();
      },
    }, 'Mint a session QR'),
    qrOut,
    qr && qr.session_id === t.session_id
      ? el('button', {
          class: 'btn btn-primary',
          onclick: () => { send('SESSION_QR', qr?.token ?? null); },
        }, `Confirm this is ${t.label}, with the QR as evidence`)
      : el('p', { class: 'note' },
          'Mint a QR for this session to confirm with it. Until then only the roster tap is offered '
          + '— a confirm button with no evidence behind it is not shown.'),
    out,
  );
}

/* ── THE CORRECTION ───────────────────────────────────────────────────────────────────────── */

function correctionPanel(academyId: string, targets: ConfirmTarget[], rerender: () => void): HTMLElement {
  const intro = [
    el('p', { class: 'note' },
      'When a ball went onto the wrong child’s record. The server MOVES the deliveries and APPENDS '
      + 'to an audit trail — nothing is deleted, and the original binding survives in the review log '
      + 'and in the consent ledger for both children.'),
    el('p', { class: 'warn' },
      'A correction is not total. Metric observations cannot be re-pointed: migration 041 freezes AI '
      + 'output, so an observation’s athlete cannot be changed. They are DISMISSED and left as rows '
      + 'under the previous athlete, and must be re-derived for the correct one. The receipt below '
      + 'says how many.'),
  ];

  if (targets.length < 2) {
    return section('Correct a wrong athlete', ...intro,
      empty('A correction needs two open sessions: the one that recorded the wrong child, and the '
        + 'correct child’s own session. Both must already exist — this screen never creates one to '
        + 'receive a correction.'));
  }

  const from = targets.find((t) => t.session_id === correctionFromId) ?? null;
  const to = targets.find((t) => t.session_id === correctionToId) ?? null;

  const pick = (which: 'from' | 'to'): HTMLElement => {
    const list = el('div', { class: 'list' });
    for (const t of targets) {
      const active = which === 'from' ? correctionFromId === t.session_id : correctionToId === t.session_id;
      list.append(el('button', {
        class: `row-item row-tap${active ? ' row-selected' : ''}`,
        onclick: () => {
          if (which === 'from') correctionFromId = t.session_id;
          else correctionToId = t.session_id;
          rerender();
        },
      },
        el('p', { class: 'mono small' }, t.label),
        field('Session', t.session_id),
        field('Net', t.lane),
        active && el('span', { class: 'tag tag-ok' }, 'selected'),
      ));
    }
    return list;
  };

  const reasonInput = el('input', {
    class: 'input', type: 'text', value: correctionReason,
    placeholder: 'what happened (recorded on the trail)',
  });
  reasonInput.addEventListener('input', () => { correctionReason = reasonInput.value; });

  const out = el('div');
  const sameAthlete = !!from && !!to && from.athlete_id === to.athlete_id;

  return section('Correct a wrong athlete', ...intro,
    el('h3', {}, 'The session that recorded the WRONG child'),
    pick('from'),
    el('h3', {}, 'The session belonging to the CORRECT child'),
    el('p', { class: 'note' },
      'The destination session must already be that child’s own. Re-pointing a delivery into a third '
      + 'athlete’s session would simply be a second misassignment, and the server refuses it.'),
    pick('to'),
    el('label', { class: 'label' }, 'Reason'), reasonInput,
    sameAthlete && el('p', { class: 'warn' },
      'Both sessions belong to the same athlete. A correction must name a different child.'),
    from && to && !sameAthlete
      ? el('button', {
          class: 'btn btn-primary',
          onclick: () => {
            void (async () => {
              mount(out, spinner('Correcting…'));
              // The affirmation names BOTH children, because both are subjects of this act.
              const affirmation = CorrectionAffirmation.affirm(
                from, to, 'ROSTER_TAP', correctionReason.trim() || null,
              );
              const r = await correctWithAffirmation(academyId, affirmation);
              if (isRefusal(r)) { mount(out, refusalView(r)); return; }
              mount(out, correctionReceiptView(r.value, from, to, rerender));
            })();
          },
        }, `Move ${from.label}’s deliveries onto ${to.label}’s record`)
      : el('p', { class: 'note' },
          'Select both sessions to correct. There is no default and no single-tap correction — this '
          + 'moves records between two children’s Passports.'),
    out,
  );
}

/**
 * THE RECEIPT, INCLUDING WHAT DID NOT MOVE.
 *
 * `observations_left_under_previous_athlete` is rendered under its own heading EVERY time, zero
 * included. A coach who reads "12 deliveries moved" and nothing else will reasonably believe the
 * record is now clean; it is not, and the work of re-deriving those observations is only going to
 * happen if somebody is told it is outstanding.
 */
export function correctionReceiptView(
  r: CorrectionReceipt, from: ConfirmTarget, to: ConfirmTarget, rerender: () => void,
): HTMLElement {
  return el('div', {},
    el('p', { class: 'ok' }, `Corrected by ${r.corrected_by} at ${r.corrected_at}.`),
    el('h3', {}, 'What moved'),
    field('Deliveries moved', r.deliveries_moved),
    field('From', `${from.label} (${r.from_athlete_id})`),
    field('To', `${to.label} (${r.to_athlete_id})`),
    field('Audit rows appended', r.reviews_appended),
    el('p', { class: 'note' },
      'Nothing was deleted. The previous binding survives in the append-only review log and in the '
      + 'consent ledger, where a rights-holder on either side can find it.'),

    el('h3', {}, 'What did NOT move'),
    field('Metric observations left under the previous athlete', r.observations_left_under_previous_athlete),
    field('Observations dismissed', r.observations_dismissed),
    el('p', { class: r.observations_left_under_previous_athlete > 0 ? 'warn' : 'note' },
      r.observations_left_under_previous_athlete > 0
        ? `${r.observations_left_under_previous_athlete} metric observation(s) still read under `
          + `${from.label} (${r.from_athlete_id}). Migration 041 freezes AI output, so an `
          + 'observation’s athlete cannot be re-pointed — these were dismissed from the effective '
          + 'values, not moved, and must be re-derived for the correct athlete. This correction was '
          + 'not total.'
        : 'No metric observations were attached to the moved deliveries, so nothing was left behind '
          + 'on this occasion. Observations can never be re-pointed, so a later correction on a '
          + 'measured session will leave some.'),
    // The service's own sentences about anything the schema would not let it finish.
    ...r.notes.map((n) => el('p', { class: 'warn' }, n)),
    el('button', { class: 'btn btn-quiet', onclick: rerender }, 'Refresh'),
  );
}
